import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default {

  init(hero) {

    // Return if hero is not found
    if(!hero) return;

    const mm = gsap.matchMedia();

    mm.add(
      {
        isSmall: "(max-width: 767.98px)",
        isMedium: "(min-width: 768px) and (max-width: 1023.98px)",
        isLarge: "(min-width: 1024px)",
      },
      (context) => {
        const { isSmall, isMedium, isLarge } = context.conditions;

        // Setup the timeline
        const tl = gsap.timeline({
          defaults: { ease: "none" },
          scrollTrigger: {
            trigger: hero,
            start: "top top",
            end: () => "+=" + getAnimationRangePx(hero),
            scrub: true,
            invalidateOnRefresh: true,
            pin: isLarge,
            pinSpacing: isLarge,
          },
        });

        // All screen animations
        // 0% -> 100%
        tl.to(
          hero,
          {
            "--hero-teaser-bar-scale": 12,
            "--hero-teaser-video-zoom": 1,
            duration: 1,
          },
          0
        );

        // Small screen animations
        if (isSmall) {

          // 0% -> 30%
          tl.to(
            hero,
            {
              "--hero-teaser-opacity": 0,
              duration: 0.3,
            },
            0
          );

          // 20% -> 30%
          tl.to(
            hero,
            {
              "--hero-teaser-video-modal-button-opacity": 1,
              duration: 0.1,
            },
            0.2
          );

        }

        // Medium screen animations
        if (isMedium) {
          // 0% -> 100%
          tl.to(
            hero,
            {
              "--hero-teaser-shift-y": "-4vw",
              duration: 1,
            },
            0
          );

          // 0% -> 30%
          tl.to(
            hero,
            {
              "--hero-teaser-opacity": 0,
              duration: 0.3,
            },
            0
          );

          // 10% -> 30%
          tl.to(
            hero,
            {
              "--hero-teaser-video-modal-button-opacity": 1,
              duration: 0.2,
            },
            0.1
          );
        }

        // Large screen animations
        if (isLarge) {

          // 0% -> 100%
          tl.to(
            hero,
            {
              "--hero-teaser-shift-y": "-10vw",
              "--hero-teaser-shift-x": "0",
              duration: 1,
            },
            0
          );

          // 5%
          tl.to(
            hero,
            {
              "--hero-teaser-content-z-index": 1,
            },
            0.05
          );

          // 0% -> 20%
          tl.to(
            hero,
            {
              "--hero-teaser-opacity": "0",
              duration: 0.2,
            },
            0
          );

          // 20% -> 50%
          tl.to(
            hero,
            {
              "--hero-teaser-video-modal-button-opacity": 1,
              duration: 0.3,
            },
            0.2
          );

          // 21%
          tl.to(
            hero,
            {
              "--hero-teaser-video-content-move": "-50vw",
            },
            0.21
          );

          // 21% -> 100%
          tl.to(
            hero,
            {
              "--hero-teaser-intro-text-opacity": 1,
              duration: 0.79,
            },
            0.21
          );
        }

        // Cleanup the timeline
        return () => {
          tl.scrollTrigger && tl.scrollTrigger.kill();
          tl.kill();
        };
      }
    );

  }

}

/**
 * Get the animation range in pixels
 * @param {HTMLElement} element - The element to get the animation range from
 * @returns {number} The animation range in pixels
 */
function getAnimationRangePx(element) {
  const styles = getComputedStyle(element);
  const raw = styles.getPropertyValue("--animation-range").trim();

  if (!raw) {
    return window.innerHeight; // safe fallback
  }

  // Matches "30vw" or "calc(30vw)" or "calc(30vw + ...)" (takes first vw value)
  const vwMatch = raw.match(/(-?[\d.]+)\s*vw/);
  if (vwMatch) {
    const vwValue = parseFloat(vwMatch[1]);
    return (window.innerWidth * vwValue) / 100;
  }

  // Simple px support: "500px" or "calc(500px)"
  const pxMatch = raw.match(/(-?[\d.]+)\s*px/);
  if (pxMatch) {
    return parseFloat(pxMatch[1]);
  }

  // Last resort: try to interpret as plain number of px
  const num = parseFloat(raw);
  if (!Number.isNaN(num)) {
    return num;
  }

  return window.innerHeight;
}
